// Chat state hook. Owns the message log, the live SSE connection to /chat,
// the per-message stream parser, and the envelope router.
//
// Faithful port of the vanilla static/app.js submit handler (DESIGN_NOTES.md
// §2), restructured around React state instead of imperative DOM mutation.

import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { type Envelope, envelopeTabId } from "./envelope.js";
import { envelopeRoute } from "./routes.js";

export type ChatRole = "user" | "assistant" | "tool" | "error";

export interface ChatMessage {
  role: ChatRole;
  text: string;          // already-rendered HTML for tool/error, raw text for user/assistant
  meta?: string;         // small label above the bubble (tool_use id, model name, …)
}

export interface PaneEntry {
  id: string;
  env: Envelope;
}

export interface ChatState {
  messages: ChatMessage[];
  pinboard: PaneEntry[];       // primary pane (canvas_pane="primary")
  sidekick: PaneEntry[];       // secondary pane (canvas_pane="secondary")
  activePinboardId: string | null;
  activeSidekickId: string | null;
  sending: boolean;
}

export interface ChatAPI extends ChatState {
  send(text: string): Promise<void>;
  setActivePinboard(id: string | null): void;
  setActiveSidekick(id: string | null): void;
  closePinTab(id: string): void;
  closeSideTab(id: string): void;
}

export function useChat(): ChatAPI {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [pinboard, setPinboard] = useState<PaneEntry[]>([]);
  const [sidekick, setSidekick] = useState<PaneEntry[]>([]);
  const [activePinboardId, setActivePinboardId] = useState<string | null>(null);
  const [activeSidekickId, setActiveSidekickId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  // The wire-format message log we send back to the backend on each turn.
  // Kept in a ref so we can mutate it synchronously inside the stream loop
  // without React re-render churn.
  // content is `string` for plain user messages and assistants without tool
  // calls, and `any[]` (array of content blocks) when an assistant turn
  // produced mcp_tool_use / mcp_tool_result blocks that have to be echoed
  // verbatim on the next turn (otherwise Anthropic hangs — Bug #11).
  const wireMessagesRef = useRef<Array<{ role: "user" | "assistant"; content: string | any[] }>>([]);

  const appendMessage = useCallback((m: ChatMessage) => {
    setMessages(prev => [...prev, m]);
  }, []);

  const routeArtifact = useCallback((env: Envelope) => {
    const pane = env.render_hints?.canvas_pane ?? "inline-only";
    const id = envelopeTabId(env);
    if (pane === "primary") {
      setPinboard(prev => {
        const next = prev.filter(e => e.id !== id);
        next.push({ id, env });
        return next;
      });
      setActivePinboardId(id);
      // Switch the main canvas to the view that natively renders this
      // artifact (e.g. render_critical_path -> /critical-path). Without
      // this the chart only appears as a small card in the right rail
      // while the main pane keeps showing whatever route the user was on.
      const target = envelopeRoute(env);
      if (target) navigate(target);
    } else if (pane === "secondary") {
      setSidekick(prev => {
        const next = prev.filter(e => e.id !== id);
        next.push({ id, env });
        return next;
      });
      setActiveSidekickId(id);
    } else {
      appendMessage({ role: "tool", text: JSON.stringify(env.data, null, 2), meta: env.title });
    }
  }, [appendMessage]);

  const send = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);

    wireMessagesRef.current.push({ role: "user", content: trimmed });
    appendMessage({ role: "user", text: trimmed });

    // Append a placeholder assistant message that we mutate in place as text
    // deltas arrive.
    let assistantIndex = -1;
    setMessages(prev => {
      assistantIndex = prev.length;
      return [...prev, { role: "assistant", text: "", meta: "claude" }];
    });

    let assistantText = "";
    // Full assistant content blocks (text + mcp_tool_use + mcp_tool_result +
    // tool_use + tool_result) captured from the final message_stop event so we
    // can echo them back to Anthropic on the next turn. Echoing only the text
    // was the cause of Bug #11 (chat hangs on follow-up): when an assistant
    // turn included tool_use blocks, dropping them on the next request made
    // Anthropic see an inconsistent history and stop responding.
    // Use a ref-shaped object so TypeScript's flow analysis doesn't narrow
    // it to `null` based on the initialiser (the closure inside
    // handleStreamEvent reassigns it, but TS doesn't follow that).
    const assistantBlocksRef: { current: any[] } = { current: [] };
    // De-dup tool_result rendering: Anthropic streams the same block at
    // content_block_start AND content_block_stop. Must be declared BEFORE the
    // stream loop or the first call to handleToolResult hits a TDZ
    // ReferenceError that kills the stream (session bug #6).
    const seenToolResults = new Set<string>();

    let resp: Response;
    try {
      resp = await fetch("/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: wireMessagesRef.current }),
      });
    } catch (e) {
      appendMessage({ role: "error", text: String(e) });
      setSending(false);
      return;
    }
    if (!resp.ok || !resp.body) {
      const err = await resp.text().catch(() => resp.statusText);
      appendMessage({ role: "error", text: err });
      setSending(false);
      return;
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buf.indexOf("\n\n")) !== -1) {
        const chunk = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        const lines = chunk.split("\n");
        let evName = "event";
        let evData = "";
        for (const l of lines) {
          if (l.startsWith("event: ")) evName = l.slice(7).trim();
          if (l.startsWith("data: ")) evData += l.slice(6);
        }
        if (!evData) continue;
        let payload: any;
        try { payload = JSON.parse(evData); } catch { continue; }
        try {
          handleStreamEvent(evName, payload);
        } catch (err) {
          // A bug in one event handler must not kill the whole stream.
          console.error("handleStreamEvent failed", evName, err);
          appendMessage({ role: "error", text: `handleStreamEvent threw on ${evName}: ${String(err)}` });
        }
      }
    }

    // Prefer pushing the full content blocks (captured at message_stop) so
    // Anthropic sees the same tool_use/tool_result history on the next turn
    // and doesn't hang. Fall back to text only if no message_stop captured
    // blocks (unexpected — stream ended without a final message).
    if (assistantBlocksRef.current.length > 0) {
      wireMessagesRef.current.push({ role: "assistant", content: assistantBlocksRef.current });
    } else if (assistantText) {
      wireMessagesRef.current.push({ role: "assistant", content: assistantText });
    }
    setSending(false);

    function handleStreamEvent(name: string, payload: any) {
      if (name === "content_block_start") {
        const block = payload.content_block;
        if (block?.type === "mcp_tool_use" || block?.type === "tool_use") {
          const helper = block.server_name ? `${block.server_name}/${block.name}` : block.name;
          appendMessage({
            role: "tool",
            text: `<em>calling ${escapeHtml(helper)}…</em>`,
            meta: `tool_use ${(block.id || "").slice(0, 16)}`,
          });
        } else if (block?.type === "mcp_tool_result" || block?.type === "tool_result") {
          handleToolResult(block);
        }
      } else if (name === "content_block_stop") {
        const block = payload.content_block;
        if (block?.type === "mcp_tool_result" || block?.type === "tool_result") {
          handleToolResult(block);
        }
      } else if (name === "content_block_delta" || payload.delta) {
        const delta = payload.delta;
        if (delta?.type === "text_delta") {
          assistantText += delta.text || "";
          setMessages(prev => {
            if (assistantIndex < 0 || assistantIndex >= prev.length) return prev;
            const next = prev.slice();
            next[assistantIndex] = { ...next[assistantIndex]!, text: assistantText };
            return next;
          });
        }
      } else if (name === "message_stop" || name === "done") {
        const blocks = payload?.message?.content || [];
        // Save the full content array so the next turn can include this
        // assistant message with its tool_use+tool_result blocks intact —
        // otherwise Anthropic sees an inconsistent history on follow-up and
        // errors with "Extra inputs are not permitted" / hangs (Bug #11).
        //
        // BUT: Anthropic's response stream decorates each block with
        // implementation details (parsed_output, cache_control, citations,
        // …) that ARE NOT VALID INPUTS — sending them back triggers a
        // 400 "Extra inputs are not permitted". So we sanitize each block
        // to the minimum field set the API accepts as input.
        if (blocks.length > 0) {
          assistantBlocksRef.current = blocks.map(sanitizeBlockForInput);
        }
        for (const b of blocks) {
          if (b.type === "mcp_tool_result" || b.type === "tool_result") handleToolResult(b);
        }
      } else if (name === "error") {
        appendMessage({ role: "error", text: JSON.stringify(payload) });
      }
    }

    function handleToolResult(b: any) {
      const dedupKey = b.tool_use_id || JSON.stringify(b).slice(0, 200);
      if (seenToolResults.has(dedupKey)) return;
      seenToolResults.add(dedupKey);
      let envelope: Envelope | null = null;
      const c = b.content;
      if (Array.isArray(c)) {
        for (const item of c) {
          const t = item.text || item.json || "";
          envelope = envelope || tryParseEnvelope(t);
        }
      } else if (typeof c === "string") {
        envelope = tryParseEnvelope(c);
      } else if (c && typeof c === "object") {
        envelope = c as Envelope;
      }
      if (envelope && envelope.artifact_type) {
        routeArtifact(envelope);
      } else {
        appendMessage({ role: "tool", text: escapeHtml(JSON.stringify(b, null, 2)), meta: "tool_result (no envelope parsed)" });
      }
    }
  }, [sending, appendMessage, routeArtifact]);

  const closePinTab = useCallback((id: string) => {
    setPinboard(prev => {
      const next = prev.filter(e => e.id !== id);
      setActivePinboardId(curr => curr === id ? (next.at(-1)?.id ?? null) : curr);
      return next;
    });
  }, []);

  const closeSideTab = useCallback((id: string) => {
    setSidekick(prev => {
      const next = prev.filter(e => e.id !== id);
      setActiveSidekickId(curr => curr === id ? (next.at(-1)?.id ?? null) : curr);
      return next;
    });
  }, []);

  return {
    messages,
    pinboard,
    sidekick,
    activePinboardId,
    activeSidekickId,
    sending,
    send,
    setActivePinboard: setActivePinboardId,
    setActiveSidekick: setActiveSidekickId,
    closePinTab,
    closeSideTab,
  };
}

function tryParseEnvelope(s: unknown): Envelope | null {
  if (!s) return null;
  if (typeof s !== "string") return s as Envelope;
  try { return JSON.parse(s) as Envelope; } catch { return null; }
}

// Anthropic returns content blocks decorated with implementation details
// (parsed_output, citations, cache_control, signature, …) that are not
// accepted as input on a follow-up turn. This whitelist keeps each block
// down to the minimum field set the Messages API documents as valid input.
function sanitizeBlockForInput(b: any): any {
  if (!b || typeof b !== "object") return b;
  switch (b.type) {
    case "text":
      return { type: "text", text: b.text ?? "" };
    case "thinking":
      // Extended-thinking blocks: api accepts type + thinking + signature.
      return {
        type: "thinking",
        thinking: b.thinking ?? "",
        ...(b.signature ? { signature: b.signature } : {}),
      };
    case "tool_use":
      return { type: "tool_use", id: b.id, name: b.name, input: b.input ?? {} };
    case "tool_result":
      return {
        type: "tool_result",
        tool_use_id: b.tool_use_id,
        content: b.content,
        ...(b.is_error ? { is_error: true } : {}),
      };
    case "mcp_tool_use":
      return {
        type: "mcp_tool_use",
        id: b.id,
        name: b.name,
        input: b.input ?? {},
        ...(b.server_name ? { server_name: b.server_name } : {}),
        ...(b.server_url ? { server_url: b.server_url } : {}),
      };
    case "mcp_tool_result":
      return {
        type: "mcp_tool_result",
        tool_use_id: b.tool_use_id,
        content: b.content,
        ...(b.is_error ? { is_error: true } : {}),
      };
    default:
      // Unknown block type — pass through but warn so we notice new shapes.
      console.warn("sanitizeBlockForInput: unknown block type", b.type);
      return b;
  }
}

function escapeHtml(s: string): string {
  return String(s ?? "").replace(/[&<>"']/g, c => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string
  ));
}
